// Currency, percent and date formatting come from @nimit9/signet-lib/format
// (Intl-based: Indian lakh/crore grouping for en-IN, no date-fns). paisa keeps
// only its INR shorthands and the privacy-mask helpers (PAISA-PRIV-01) here.
import {
  createCompactCurrencyFormatter,
  createCurrencyFormatter,
  createDateFormatter,
  createDateShortFormatter,
} from "@nimit9/signet-lib/format"

export {
  createCompactCurrencyFormatter,
  createCurrencyFormatter,
  createDateFormatter,
  createDateShortFormatter,
  formatPercent,
} from "@nimit9/signet-lib/format"

const inr = createCurrencyFormatter("INR", "en-IN")

/** ₹ with Indian grouping: whole rupees, or paise with `precise`. */
export function formatINR(amount: number | string, precise = false): string {
  return inr(amount, precise)
}

/** ₹1.5L / ₹2Cr below which it falls back to formatINR. */
export function formatINRCompact(amount: number | string, decimals = 1): string {
  return createCompactCurrencyFormatter("INR", "en-IN", decimals)(amount)
}

/** "5 Mar 2026" in en-IN. */
export const formatDate = createDateFormatter("en-IN")

/** "5 Mar" in en-IN. */
export const formatDateShort = createDateShortFormatter("en-IN")

/**
 * The masked stand-in for an amount (PAISA-PRIV-01): the currency symbol plus
 * fixed placeholder dots, e.g. "₹ ••••••". Same text for every value, so masked
 * rows keep one width and nothing about the real number reaches the DOM.
 */
export function maskedAmountText(currency = "INR", locale = "en-IN"): string {
  let symbol = "₹"
  try {
    symbol =
      new Intl.NumberFormat(locale, { style: "currency", currency })
        .formatToParts(0)
        .find((p) => p.type === "currency")?.value ?? symbol
  } catch {
    // unknown currency/locale — keep the default symbol
  }
  return `${symbol} ••••••`
}

/** Placeholder for a data-derived percentage while amounts are masked. */
const MASKED_PERCENT = "••%"

/**
 * Replace every percentage in a sentence ("73%", "+12.5 %", "−4%") with the
 * masked placeholder. Ratios of amounts (savings rate, % of budget, share of
 * spend) leak as much as the amounts, so the mask hides them too.
 */
export function maskPercentText(text: string): string {
  return text.replace(/[+\-−]?\d[\d,]*(?:\.\d+)?\s?%/g, MASKED_PERCENT)
}
